import { Component, ViewChild, OnInit, HostListener } from '@angular/core';
import { Router, NavigationError, NavigationEnd } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatSidenav } from '@angular/material/sidenav';
import { environment } from '../environments/environment';

import { TranslateService } from '@ngx-translate/core';
import { CommonService, LangCodeTypeEnum } from './services/common.service';
import { ReportsService } from './services/reports.service';
import { AuthService } from './modules/authentication/services/auth.service';
import { SwUpdate } from '@angular/service-worker';
import { first } from 'rxjs/operators';

import { Config } from '../app/config';
import { logoSpecificSize } from './data/asset-data';
import { DynamicScript, dynamicScripts } from './data/dynamic-script-data';
import { CustomContentService } from './services/custom-content.service';
import { MisNotificationsComponent } from './dialogs/mis-notifications/mis-notifications.component';

declare let gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title: string = 'VRM - eDriving';
  inFullscreenMode: boolean = false;
  bespokeLogoSize = logoSpecificSize.sizeInPx;

  dynamicScripts: Array<DynamicScript> = dynamicScripts;

  @ViewChild('mainSidenav')
  mainSidenav: MatSidenav;

  @ViewChild('settingsSidenav')
  settingsSidenav: MatSidenav;

  constructor(
    public cService: CommonService,
    public authService: AuthService,
    private rptService: ReportsService,
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private appUpdate: SwUpdate,
    private router: Router,
    private cContentService: CustomContentService
  ) {
    this.cService.processAndLoadDynamicScriptsOnDemand('global');
    this.constructorInit();
  }

  constructorInit(): void {
    this.initializeVrmTranslations();

    // Orientation based Breakpoint
    this.breakpointObserver.observe([Breakpoints.HandsetLandscape, Breakpoints.HandsetPortrait]).subscribe((result) => {
      this.cService.applyBreakpoint();
    });

    if (this.authService.isAuthenticated()) {
      this.cService.setPageHeader('');
      this.authService.determineDefaultStartPage(this.cService.userProfile.startPage);
      this.cService.triggerUserProfileReadyObservable(this.cService.userProfile);

      this.cService.appStableSinceInitLoad$.pipe(first((stable) => stable)).subscribe({
        next: (appStatus) => {
          console.log('%c Application stable', 'color: salmon', appStatus);

          // Refresh token but allow time for all initial API calls to finish
          setTimeout(() => {
            this.authService.refreshToken().subscribe({
              next: () => {
                this.authService.triggerTokenExpirationAndKeepaliveWatch(this.cService.userProfile.tokenTtl);
              },
            });
          }, this.cService.appSettings.waitInMsForInitialApiCalls);

          // Warn if using live-data in development
          if (!environment.production && Config.apiRoot.env.toLowerCase() === 'production') {
            this.cService.prodDataInUseInDev = true;
          }
        },
      });

      // Check updates via SW
      if (this.appUpdate.isEnabled) {
        this.appUpdate.checkForUpdate().then((updateAvailable) => {
          if (updateAvailable) {
            this.appUpdate.activateUpdate().then(() => {
              this.cService.showToastWithoutDuration('Updates available', 'refresh now');
            });
          }
        });
      }
    }
  }

  // Lifecycle Hooks
  ngOnInit(): void {
    // Apply User Profile if not already applied
    if (this.authService.isAuthenticated()) {
      if (!this.authService.userProfileSet) {
        this.cService.userProfile = JSON.parse(localStorage.getItem(this.cService.localStorageKeys.userProfile));
        this.cService.applyUserProfile();

        console.log('User Profile', this.cService.userProfile);
      } else {
        this.cService.processMainNavTranslations();
      }
    } else {
      const pathsToExcludeFromRedirect: string[] = ['/sso', '/cognito-sso', '/reset-password', '/fleet_activation'];
      if (!pathsToExcludeFromRedirect.some((path: string) => location.href.includes(path))) {
        this.authService.navigateToLoginPage();
      }
    }

    if (localStorage.getItem(this.cService.localStorageKeys.isDarkTheme)) {
      this.cService.isDarkTheme = true;
    }

    const savedTheme = localStorage.getItem(this.cService.localStorageKeys.currentTheme);
    if (savedTheme) {
      this.cService.currentTheme = savedTheme;
    }

    this.router.events.subscribe({
      next: (event: any) => {
        if (event instanceof NavigationEnd) {
          this.cService.triggerPageChangedNavEndObservable();
          this.cService.getSelectedNav();

          // PRODUCTION ONLY
          if (environment.production) {
            // Google analytics page tracking
            if (this.cService.userProfile && this.cService.userProfile.userId && event.urlAfterRedirects) {
              gtag('config', 'UA-158782147-3', {
                user_id: this.cService.userProfile.userId,
                page_path: event.urlAfterRedirects,
              });
            }
          }
        }
      },
    });

    // show notifications
    if (this.authService.isAuthenticated() && !this.cService.misNotificationsHidden()) {
      this.cContentService.getNotifications().subscribe({
        next: (misNotifications: any) => {
          const dialogSettings = this.cContentService.notificationViewDialogSettings;
          dialogSettings.data = {
            contents: misNotifications.data,
            contentType: this.cContentService.contentTypes.notifications,
          };

          if (dialogSettings.data.contents.length) {
            this.cContentService.dialog.open(MisNotificationsComponent, dialogSettings);
          }

          console.log('%cNOTIFICATION DISPLAY DATA', 'color: lime', {
            misNotifications,
            autoDisplayNotifications: dialogSettings.data.contents,
          });
        },
      });
    }
  }

  private initializeVrmTranslations() {
    this.plugAppLangIntoDocumentLang();

    // ngx - Translation - NOTE: Lang to use is also set on Login
    if (this.cService.vrmLanguages && this.cService.vrmLanguages.length) {
      this.setTranslations();
    } else {
      this.cService.getLanguageList().subscribe({
        next: () => {
          this.setTranslations();
        },
      });
    }
  }

  private setTranslations() {
    const vrmLanguages: Array<string> = [];

    this.cService.vrmLanguages.map((vrmLang) => {
      vrmLanguages.push(vrmLang.code);
    });
    this.translate.addLangs(vrmLanguages);

    this.translate.setDefaultLang('en');
    this.rptService.applyAgGridLocale();
  }

  // EVENTS
  @HostListener('window:offline')
  onOffline() {
    this.cService.isOnline = false;
  }

  @HostListener('window:online')
  onOnline() {
    this.cService.isOnline = true;
  }

  toggleFullScreen() {
    if (!this.inFullscreenMode) {
      const elem: any = document.documentElement;

      if (elem.requestFullscreen) {
        elem.requestFullscreen();
        this.inFullscreenMode = true;
      } else if (elem.mozRequestFullScreen) {
        /* Firefox */
        elem.mozRequestFullScreen();
        this.inFullscreenMode = true;
      } else if (elem.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        elem.webkitRequestFullscreen();
        this.inFullscreenMode = true;
      } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        elem.msRequestFullscreen();
        this.inFullscreenMode = true;
      }
    } else {
      this.exitFullScreen();
    }
  }

  exitFullScreen() {
    const mainDocument: any = document;

    if (mainDocument.exitFullscreen) {
      mainDocument.exitFullscreen();
      this.inFullscreenMode = false;
    } else if (mainDocument.mozCancelFullScreen) {
      /* Firefox */
      mainDocument.mozCancelFullScreen();
      this.inFullscreenMode = false;
    } else if (mainDocument.webkitExitFullscreen) {
      /* Chrome, Safari and Opera */
      mainDocument.webkitExitFullscreen();
      this.inFullscreenMode = false;
    } else if (mainDocument.msExitFullscreen) {
      /* IE/Edge */
      mainDocument.msExitFullscreen();
      this.inFullscreenMode = false;
    }
  }

  // ---- METHODS ----
  manuallyCheckUpdates() {
    if (this.appUpdate.isEnabled) {
      this.cService.showToast(`${this.cService.readyTranslations.messagesAndWarnings.checkingUpdates}...`);

      this.appUpdate.checkForUpdate().then((updateAvailable) => {
        if (updateAvailable) {
          location.reload();
        }
      });
    } else {
      this.cService.showToast(':: Feature not available . Clear cache and refresh instead');
    }
  }

  reloadApp() {
    const translatedMessage = this.cService.readyTranslations.generic.reloading
      ? this.cService.readyTranslations.generic.reloading
      : 'Reloading';

    this.cService.showToast(translatedMessage + '...');
    this.cService.reloadAndTryToApplyUpdates();
  }

  closeMainSidenav(): void {
    this.mainSidenav.close();
  }

  toggleSettingsSidenav(accAndSecTab?: number): void {
    if (accAndSecTab) {
      this.authService.selectedAccountAndSecTab = accAndSecTab;
    }

    this.settingsSidenav.toggle();

    if (!this.cService.isPcViewport) {
      this.cService.mainSidenavOpened = false;
    }
  }

  closeSettingSidenav(): void {
    this.settingsSidenav.close();
  }

  logout() {
    this.authService.logout();
    this.authService.navigateToLoginPage();
  }

  toggleDarkTheme(): void {
    this.cService.isDarkTheme = !this.cService.isDarkTheme;

    if (this.cService.isDarkTheme) {
      localStorage.setItem(this.cService.localStorageKeys.isDarkTheme, 'true');
    } else {
      localStorage.removeItem(this.cService.localStorageKeys.isDarkTheme);
    }
  }

  identifySelectedNav(selectedNav?: any): void {
    this.cService.commonReportTitle = selectedNav.translatedName;
    this.cService.identifySelectedNavAndParent();

    if (this.cService.isMobileViewport) {
      this.cService.toggleMainSidenav();
    }
  }

  private plugAppLangIntoDocumentLang() {
    let cachedLangCode: string;

    cachedLangCode = localStorage.getItem(this.cService.localStorageKeys.language);

    if (cachedLangCode) {
      document.documentElement.lang = this.cService.convertLanguageCode(cachedLangCode, LangCodeTypeEnum.iso);
    }
  }

  onlyBadge(event: Event) {
    event.preventDefault();
  }
}
